import { alpha, Box } from "@mui/material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import SystemButton from "../../../../../components/UIToolkit/SystemButton/SystemButton";
import { ALPHA, CONTROL_HEIGHT, FONT_SIZE } from "../../../../../theme/tokens";
import { DAYS_IN_WEEK, isWeekdayCode, WEEKDAY_PRESETS, type WeekdayCode } from "./autoApproveConsts";
import { sameDays } from "./ruleSummary";

const F = "department.config.staffApp.autoApprove.fields";

type Props = { shiftDays: string[]; onChange: (shiftDays: WeekdayCode[]) => void; testIdPrefix: string; index: number };

/** Weekdays / Weekend / Every day shortcuts, quiet beside the Days label; the one the rule matches stays highlighted. */
export const AutoApproveDayPresets: FC<Props> = ({ shiftDays, onChange, testIdPrefix, index }) => {
	const { t } = useTranslation();
	const days = shiftDays.filter(isWeekdayCode);
	const presets: { id: string; label: string; days: WeekdayCode[]; active: boolean }[] = [
		{ id: "weekdays", label: t(`${F}.days.weekdays`), days: WEEKDAY_PRESETS.weekdays, active: sameDays(days, WEEKDAY_PRESETS.weekdays) },
		{ id: "weekend", label: t(`${F}.days.weekend`), days: WEEKDAY_PRESETS.weekend, active: sameDays(days, WEEKDAY_PRESETS.weekend) },
		{ id: "every", label: t(`${F}.days.every`), days: [], active: days.length === 0 || days.length === DAYS_IN_WEEK },
	];

	return (
		<Box sx={{ display: "flex", gap: 0.25 }} data-testid={`${testIdPrefix}-day-presets-${index}`}>
			{presets.map((preset) => (
				<SystemButton
					key={preset.id}
					buttonVariant="borderless"
					size="small"
					noFullWidthMobile
					aria-pressed={preset.active}
					onClick={() => onChange(preset.days)}
					data-testid={`${testIdPrefix}-day-preset-${preset.id}-${index}`}
					sx={(theme) => ({
						minHeight: CONTROL_HEIGHT.MICRO,
						height: CONTROL_HEIGHT.MICRO,
						px: 0.75,
						fontSize: FONT_SIZE.CAPTION,
						color: "primary.dark",
						...(preset.active && { backgroundColor: alpha(theme.palette.primary.main, ALPHA.CONTROL_TINT_HOVER) }),
					})}
				>
					{preset.label}
				</SystemButton>
			))}
		</Box>
	);
};
